import { useAtomValue } from "jotai/utils";
import PageSection from "../../components/page-section";
import FacetedSearchSubjects from "../../components/search/faceted-search-subjects";
import { useSearch } from "../../hooks/useSearch";

import pageAtomFamily from "../../data/atoms/page";

import { Heading, Text } from "../../components/typography";

import { useEffect, useMemo } from "react";
import Link from "../../components/link";
import Image from "../../components/image";
import useScrollIntoView from "../../hooks/useScrollIntoView";

import "./index.css";
import { Alert, Space } from "antd";
import Metadata from "../../components/metadata";
import SocialMediaMetadata from "../../components/social-media";
import NotFound from "../not-found";

function Home() {
  const { data: page } = useAtomValue(pageAtomFamily("home"));

  const { refreshFacets, isHealthy } = useSearch();

  useScrollIntoView();

  useEffect(
    () => {
      const load = async () => {
        if (isHealthy === true) {
          await refreshFacets();
        }
      };
      load();
    },
    // eslint-disable-next-line
    [isHealthy],
  );

  const heroSection = useMemo(
    () => page.sections.find((section) => section.hash === "hero"),
    [page],
  );

  const imageStyle = useMemo(() => {
    const _style = { position: "absolute", zIndex: 10 };
    if (heroSection && heroSection.image) {
      _style[heroSection.image.align ? heroSection.image.align : "left"] =
        "0em";
      _style[
        heroSection.image.verticalAlign
          ? heroSection.image.verticalAlign
          : "top"
      ] = heroSection.image.offset ? heroSection.image.offset : "0em";
      _style.objectFit = heroSection.image.objectFit
        ? heroSection.image.objectFit
        : "contain";
      _style["height"] = heroSection.image.height
        ? heroSection.image.height
        : "100%";
      _style["width"] = heroSection.image.width
        ? heroSection.image.width
        : "50%";
    }
    return _style;
  }, [heroSection]);

  if (!page) return <NotFound />;

  // const heroSection = page.data.sections.find(
  //   (section) => section.hash === "hero",
  // );
  return (
    <div className="home-page">
      <Metadata
        jsonld={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          url: process.env.REACT_APP_URL || window.location.origin,
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate:
                (process.env.REACT_APP_URL || window.location.origin) +
                "/search/?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <SocialMediaMetadata
        title="B.C. Open Collection by BCcampus"
        description="B.C. Open Collection is a selection of assets that educators throughout the province can access for use in their classrooms, with their institution's Learning Management System (LMS)."
        // image={page.data.socialMediaImage || page.data.image?.file}
        image={page.image}
      />
      {heroSection && (
        <header>
          <PageSection
            id="page-hero"
            type="hero-dark"
            style={{ paddingTop: "3em", height: "22em", textAlign: "center" }}
          >
            <Image
              alt="Hero"
              format="small"
              data={heroSection.image.file}
              style={{ ...imageStyle }}
              disableReactSVG
            />
            <Heading level={1} style={{ marginBottom: "0rem" }}>
              {heroSection.title}
            </Heading>

            <div
              className="bccampus-typography-paragraph"
              style={{ marginBottom: "2em" }}
              dangerouslySetInnerHTML={{ __html: heroSection.content }}
            />

            {(heroSection && heroSection.actions.length) > 0 && (
              <div className="bccampus-page-section-hero-actions">
                <Space size={14} direction="vertical">
                  {heroSection.actions.map((action, index) => (
                    <Link
                      key={index}
                      type={action.linkStyle}
                      href={action.url}
                      image={action.image}
                      iconAlign={action.iconAlign}
                    >
                      {action.label}
                    </Link>
                  ))}
                </Space>
              </div>
            )}
          </PageSection>
        </header>
      )}
      <PageSection expandable id="find-by-subject">
        <Heading level={2}>Find Materials by Subject</Heading>
        {isHealthy === true ? (
          <FacetedSearchSubjects />
        ) : isHealthy === false ? (
          <Alert
            type="warning"
            showIcon
            message={
              <Text>
                We are currently experiencing a technical difficulty with our
                search engine and working to solve the issue. Sorry for the
                inconvenience.
              </Text>
            }
          />
        ) : (
          <Alert
            style={{ backgroundColor: "#ffffff" }}
            type="info"
            showIcon
            message={
              <Text>
                It seems our search engine is having difficulty responding to
                us; please, wait a bit while we retry.
              </Text>
            }
          />
        )}
      </PageSection>
      <main id="how-it-works" tabIndex={-1}>
        {page.sections
          .filter((section) => section.hash !== "hero")
          .map((section, index) => (
            <PageSection
              key={section.hash}
              id={section.hash}
              className="home-page-info-section"
              image={section.image}
              disableReactSVG
              type={index % 2 === 0 ? "alternate" : undefined}
            >
              <Heading level={2} feature>
                {section.title}
              </Heading>
              <div
                className="bccampus-cms-content"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </PageSection>
          ))}
      </main>
    </div>
  );
}

export default Home;
